import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { AuthGuard } from '../components/common/AuthGuard'

const Login: React.FC = () => {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')

  const { signIn, isLoading, error, clearError } = useAuth()
  const navigate = useNavigate()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Clear any previous errors
    if (error) {
      clearError()
    }

    try {
      const result = await signIn({ email, password })
      if (result.isSignedIn) {
        navigate('/dashboard')
      }
    } catch (err) {
      console.error('Login error:', err)
      // Error is already handled by the AuthContext
    }
  }

  return (
    <AuthGuard requireAuth={false}>
      <div className="login-container flex">
        {/* Brand Section */}
        <div className="brand-section hidden lg:flex lg:w-1/2 relative">
          {/* Decorative elements */}
          <div className="decorative-element"></div>
          <div className="decorative-element"></div>
          <div className="decorative-element"></div>

          <div className="brand-content flex flex-col justify-center items-center h-full p-12 text-white relative z-10">
            <div className="text-center max-w-md">
              <div className="w-20 h-20 bg-white/20 rounded-2xl flex items-center justify-center mb-8 mx-auto backdrop-blur-sm border border-white/10">
                <i className="fa-solid fa-users text-3xl text-white"></i>
              </div>

              <h1 className="text-4xl font-bold mb-6">TalentMatch</h1>
              <p className="text-xl mb-8 text-white/90">
                Revoluciona tu proceso de selección con inteligencia artificial
              </p>
            </div>
          </div>
        </div>

        {/* Login Form Section */}
        <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
          <div className="login-form-container w-full max-w-md p-8 rounded-3xl shadow-strong">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-gradient-to-r from-primary-600 to-primary-700 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-soft">
                <i className="fa-solid fa-user text-white text-2xl"></i>
              </div>
              <h2 className="text-3xl font-bold text-secondary-900 mb-2">
                Bienvenido
              </h2>
              <p className="text-secondary-600">Inicia sesión para continuar</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="form-element">
                <div className="input-group">
                  <input
                    type="email"
                    placeholder="Correo electrónico"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    required
                    disabled={isLoading}
                    className="modern-input w-full"
                  />
                  <i className="input-icon fa-solid fa-envelope"></i>
                </div>
              </div>

              <div className="form-element">
                <div className="input-group">
                  <input
                    type="password"
                    placeholder="Contraseña"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    required
                    disabled={isLoading}
                    className="modern-input w-full"
                  />
                  <i className="input-icon fa-solid fa-lock"></i>
                </div>
              </div>

              {error && (
                <div className="form-element bg-red-50 border border-red-200 rounded-xl p-4 animate-pulse">
                  <div className="flex items-center space-x-3">
                    <i className="fa-solid fa-exclamation-triangle text-red-600 text-lg"></i>
                    <div>
                      <p className="text-red-700 font-medium">{error}</p>
                      <p className="text-red-600 text-xs mt-1">
                        Verifique sus credenciales e intente nuevamente
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="form-element">
                <button
                  type="submit"
                  disabled={isLoading || !email || !password}
                  className="login-button w-full py-4 text-white font-semibold rounded-xl relative overflow-hidden"
                >
                  {isLoading && (
                    <div className="loading-overlay">
                      <div className="loading-spinner"></div>
                    </div>
                  )}
                  <span className={isLoading ? 'opacity-0' : ''}>
                    {isLoading ? 'Iniciando sesión...' : 'Iniciar sesión'}
                  </span>
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </AuthGuard>
  )
}

export default Login
